import { Controller, FieldPath, useFormContext } from 'react-hook-form';
import { Divider, Input } from '@ds';
import { FormBlockExpander } from '@lp/features/Scheduler/components';
import { CSSchedulerSettingsPeriodRulesFormType } from '@lp/features/Scheduler/SchedulerClinicalSpecialistsSettings/types';
import { getNumberInputValue, getNumberInputValueHandler } from '@lp/features/Scheduler/utils';

type RenderInputProps = {
  name: FieldPath<CSSchedulerSettingsPeriodRulesFormType>;
  classString: string;
  label?: string;
};

type CSSchedulerSettingsPeriodRulesFormProps = {
  isReadOnly: boolean;
};

/**
 * The period counterpart of CSSchedulerSettingsRulesForm.
 * Shifts per period, weekend-off minimum, no-call days and the vacation deduction are all absent
 * here on purpose - the absence-deduction table supersedes them on the period planning path.
 */
export const CSSchedulerSettingsPeriodRulesForm = ({ isReadOnly }: CSSchedulerSettingsPeriodRulesFormProps) => {
  const { control } = useFormContext<CSSchedulerSettingsPeriodRulesFormType>();

  const renderInput = (inputParams: RenderInputProps[]) =>
    inputParams.map((params) => (
      <Controller
        key={params.name}
        name={params.name}
        control={control}
        render={({ field: { value, onChange, ...field }, fieldState: { invalid, error } }) => (
          <Input
            type="number"
            aria-label={params.name}
            isInvalid={invalid}
            isDisabled={isReadOnly}
            errorMessage={error?.message}
            className={params.classString}
            label={params.label || ''}
            placeholder={params.label || ''}
            value={getNumberInputValue(value as number)}
            onValueChange={getNumberInputValueHandler(onChange)}
            {...field}
          />
        )}
      />
    ));

  return (
    <div className="flex flex-col gap-5">
      <FormBlockExpander title="Individual Workload Guardrails">
        <div className="flex gap-3 flex-col">
          <span className="text-base-5 text-l-m">On-Call Days In-Row</span>
          <div className="flex gap-5">
            {renderInput([
              {
                name: 'onCallInRow.hardMin',
                classString: 'max-w-[140px]',
                label: 'Hard Min.',
              },
              {
                name: 'onCallInRow.recommendedMin',
                classString: 'max-w-[140px]',
                label: 'Recommended Min.',
              },
              {
                name: 'onCallInRow.recommendedMax',
                classString: 'max-w-[140px]',
                label: 'Recommended Max.',
              },
              {
                name: 'onCallInRow.hardMax',
                classString: 'max-w-[140px]',
                label: 'Hard Max.',
              },
            ])}
          </div>
        </div>
      </FormBlockExpander>

      <FormBlockExpander title="Group Workload Guardrails">
        <div className="flex gap-4 flex-col text-sm">
          <span>
            CSs are assigned to 2 groups: A, B and C (or A and B, depending on hub size). The groups alternate stat
            holidays on an annual basis.
          </span>
          <Divider />
          <span>
            If a CS works on a company holiday, they also must work the day before and the day after (e.g. if they work
            on Christmas Day, they must work on Dec. 24 and Dec. 26).
          </span>
          <Divider />
          <div className="flex items-center">
            <span>Each company holiday worked grants +</span>
            {renderInput([{ name: 'holidayNoCallBonus', classString: 'max-w-[85px] ml-3 mr-3' }])}
            <span>Floating Holiday (a no-call day) to be used within 30 days.</span>
          </div>
        </div>
      </FormBlockExpander>
    </div>
  );
};
